import React, { useState } from 'react';

interface HIITCircuitExercise {
  name: string;
  reps: string | number;
  duration?: number;
  completed: boolean;
}

interface HIITCircuit {
  circuitNumber: number;
  totalRounds: number;
  completedRounds: number;
  exercises: HIITCircuitExercise[];
  restBetweenRounds?: number;
}

interface EMOMDetails {
  totalMinutes: number;
  completedMinutes: number;
  exercises: { name: string; reps: string; minuteAssignment?: number }[];
  avgTimePerMinute?: number;
}

interface AMRAPDetails {
  totalDuration: number; // seconds
  roundsCompleted: number;
  exercises: { name: string; reps: string }[];
  roundTimes?: number[]; // seconds for each round
}

interface ExpandableHIITBreakdownProps {
  hiitType: 'circuit' | 'emom' | 'amrap';
  circuits?: HIITCircuit[];
  emom?: EMOMDetails;
  amrap?: AMRAPDetails;
}

const formatTime = (totalSeconds: number | undefined): string => {
  if (!totalSeconds || !isFinite(totalSeconds)) return '--:--';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const ExpandableHIITBreakdown: React.FC<ExpandableHIITBreakdownProps> = ({
  hiitType,
  circuits,
  emom,
  amrap,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const renderCircuitBreakdown = () => {
    if (!circuits || circuits.length === 0) return null;

    return (
      <div className="space-y-4">
        {circuits.map((circuit, circuitIdx) => (
          <div key={circuitIdx} className="bg-gray-900 rounded-lg p-4 border border-blue-500">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-lg font-bold text-white">Circuit {circuit.circuitNumber}</h4>
              <span className="text-sm text-gray-300">
                {circuit.completedRounds} / {circuit.totalRounds} Rounds
              </span>
            </div>

            {/* Progress Bar */}
            <div className="mb-3">
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${(circuit.completedRounds / circuit.totalRounds) * 100}%` }}
                />
              </div>
            </div>

            {/* Exercises */}
            <div className="space-y-2">
              {circuit.exercises.map((exercise, exerciseIdx) => (
                <div
                  key={exerciseIdx}
                  className={`flex items-center justify-between p-2 rounded ${
                    exercise.completed ? 'bg-green-900/30 border-l-2 border-green-500' : 'bg-gray-800'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {exercise.completed ? (
                      <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <div className="w-5 h-5 border-2 border-gray-600 rounded-full" />
                    )}
                    <span className="text-white font-medium">{exercise.name}</span>
                  </div>
                  <span className="text-sm text-gray-400">{exercise.reps} reps</span>
                </div>
              ))}
            </div>

            {circuit.restBetweenRounds && (
              <div className="mt-3 text-xs text-gray-500 text-center">
                Rest: {circuit.restBetweenRounds}s between rounds
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderEMOMBreakdown = () => {
    if (!emom) return null;

    return (
      <div className="bg-gray-900 rounded-lg p-4 border border-purple-500">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-lg font-bold text-white">EMOM Breakdown</h4>
          <span className="text-sm text-gray-300">
            {emom.completedMinutes} / {emom.totalMinutes} Minutes
          </span>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="w-full bg-gray-700 rounded-full h-3">
            <div
              className="bg-purple-500 h-3 rounded-full transition-all"
              style={{ width: `${(emom.completedMinutes / emom.totalMinutes) * 100}%` }}
            />
          </div>
        </div>

        {/* Exercise List */}
        <div className="space-y-2">
          {emom.exercises.map((exercise, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-gray-800 rounded">
              <div>
                <div className="text-white font-medium">{exercise.name}</div>
                {exercise.minuteAssignment && (
                  <div className="text-xs text-purple-400">Minute {exercise.minuteAssignment}</div>
                )}
              </div>
              <span className="text-sm text-gray-400">{exercise.reps}</span>
            </div>
          ))}
        </div>

        {emom.avgTimePerMinute && (
          <div className="mt-4 pt-3 border-t border-gray-700 text-center">
            <div className="text-sm text-gray-400">Avg Time/Minute</div>
            <div className="text-xl font-bold text-white">{formatTime(emom.avgTimePerMinute)}</div>
          </div>
        )}
      </div>
    );
  };

  const renderAMRAPBreakdown = () => {
    if (!amrap) return null;

    return (
      <div className="bg-gray-900 rounded-lg p-4 border border-green-500">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-lg font-bold text-white">AMRAP Results</h4>
          <span className="text-sm text-gray-300">{formatTime(amrap.totalDuration)} Total</span>
        </div>

        {/* Rounds Completed */}
        <div className="mb-4 text-center">
          <div className="text-5xl font-bold text-white mb-1">{amrap.roundsCompleted}</div>
          <div className="text-sm text-gray-400">Rounds Completed</div>
        </div>

        {/* Exercise List */}
        <div className="mb-4">
          <div className="text-xs text-gray-400 font-medium mb-2">Per Round:</div>
          <div className="space-y-1">
            {amrap.exercises.map((exercise, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-gray-800 rounded text-sm">
                <span className="text-white">{exercise.name}</span>
                <span className="text-gray-400">{exercise.reps}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Round Times */}
        {amrap.roundTimes && amrap.roundTimes.length > 0 && (
          <div className="pt-3 border-t border-gray-700">
            <div className="text-xs text-gray-400 font-medium mb-2">Round Times:</div>
            <div className="grid grid-cols-3 gap-2">
              {amrap.roundTimes.slice(0, 6).map((time, idx) => ( // Show first 6 rounds
                <div key={idx} className="text-center p-2 bg-gray-800 rounded">
                  <div className="text-xs text-gray-500">R{idx + 1}</div>
                  <div className="text-sm text-white font-mono">{formatTime(time)}</div>
                </div>
              ))}
            </div>
            {amrap.roundTimes.length > 6 && (
              <div className="text-xs text-gray-500 text-center mt-2">
                +{amrap.roundTimes.length - 6} more rounds
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const getTypeLabel = () => {
    switch (hiitType) {
      case 'circuit':
        return 'Circuit Training';
      case 'emom':
        return 'EMOM (Every Minute On the Minute)';
      case 'amrap':
        return 'AMRAP (As Many Rounds As Possible)';
      default:
        return 'HIIT Workout';
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4 mt-4">
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-lg font-semibold text-white">{getTypeLabel()}</h3>
        <span className="text-gray-400">{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && (
        <div className="mt-4">
          {hiitType === 'circuit' && renderCircuitBreakdown()}
          {hiitType === 'emom' && renderEMOMBreakdown()}
          {hiitType === 'amrap' && renderAMRAPBreakdown()}
        </div>
      )}
    </div>
  );
};

